import { AccessTime, Add, CheckCircle, Delete, DragIndicator, ExpandMore, Link, OpenInNew, Save } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigation } from "contexts/notificationsContext";
import { isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { normalizeFormData } from "./utils/dirtyState";
import {
  atualizarSubtarefa,
  criarSubtarefa,
  excluirSubtarefa,
  ICriarSubtarefaDetalheRequest,
  ISubtarefaDetalhada,
  reordenarSubtarefasTarefa,
  IReordenarSubtarefasTarefaRequest,
} from "services/modules/tarefaDetalhe";

import {
  getResponsaveisFiltro,
  getStatusFiltro,
  IResponsavelFiltro,
  IStatusFiltro,
} from "services/modules/quadroTarefas";
import { useAuth } from "shared/hooks/auth";
import { decimalToHHMM, segundosToHHMMSS } from "shared/helpers/horasHHMM";
import { InputHoraHHMM } from "components/InputHoraHHMM";
import { ModalVincularTarefa } from "./Modais/ModalVincularTarefa";

/**
 * Item 6.5 — Formata o tempo Real a partir de totalSegundosReal (segundos inteiros)
 * no padrão hh:mm:ss. Fallback: totalMinutosReal*60 e, por fim, horasReal*3600.
 */
const formatTempoReal = (item: {
  totalSegundosReal?: number;
  totalMinutosReal?: number;
  horasReal?: number;
}): string => {
  const segundos =
    item.totalSegundosReal != null
      ? item.totalSegundosReal
      : item.totalMinutosReal != null
      ? item.totalMinutosReal * 60
      : Math.round((item.horasReal || 0) * 3600);
  return segundosToHHMMSS(segundos);
};

interface PainelSubtarefasProps {
  idTarefa: number;
  subtarefas: ISubtarefaDetalhada[];
  onRefresh: () => void;
  onOpenSubtarefa?: (idSubtarefa: number) => void;
  // N13.3: referência da tarefa-pai (responsáveis cross-empresa)
  idReferencia?: number | null;
  // Item 4: a tarefa-pai é de tipologia? (a Etapa foi removida na FASE 04 — item 3)
  ehTipologia?: boolean;
}

interface FormSubtarefa {
  nome: string;
  descricao: string;

  idTarefaStatus: number | "";
  previsaoInicio: string;
  previsaoTermino: string;
  estimativaHoras: number | "";
  idsResponsaveis: number[];
}

const formInicial: FormSubtarefa = {
  nome: "",
  descricao: "",

  idTarefaStatus: "",
  previsaoInicio: "",
  previsaoTermino: "",
  estimativaHoras: "",
  idsResponsaveis: [],
};

// Componente SortableAccordion para permitir drag and drop
interface SortableAccordionProps {
  id: string;
  subtarefa: ISubtarefaDetalhada;
  expanded: string | false;
  onExpandChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onDelete: (idSubtarefa: number) => void;
  onOpen?: (idSubtarefa: number) => void;
  getStatusColor: (idStatus: number) => "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
}

const SortableAccordion = ({
  id,
  subtarefa,
  expanded,
  onExpandChange,
  onDelete,
  onOpen,
  getStatusColor,
}: SortableAccordionProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "#f5f5f5" : "inherit",
  };

  return (
    <Accordion
      ref={setNodeRef}
      style={style}
      key={subtarefa.idSubtarefa}
      expanded={expanded === `panel-${subtarefa.idSubtarefa}`}
      onChange={onExpandChange(`panel-${subtarefa.idSubtarefa}`)}
      disableGutters
      elevation={0}
      sx={{
        mb: 1,
        border: 1,
        borderColor: "divider",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 0.5 } }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          pr={1}
        >
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            {/* Ícone de drag */}
            <Box
              {...attributes}
              {...listeners}
              sx={{
                cursor: isDragging ? "grabbing" : "grab",
                display: "flex",
                alignItems: "center",
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                },
              }}
            >
              <DragIndicator fontSize="small" />
            </Box>
            {/* Ícone de conclusão - aparece quando sub-tarefa está concluída */}
            {subtarefa.idTarefaStatus === 4 || subtarefa.nomeStatus.toLowerCase().includes("conclu") ? (
              <CheckCircle
                sx={{
                  color: "success.main",
                  fontSize: "1.2rem",
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
              Sub-tarefa {subtarefa.idSubtarefa} - {subtarefa.nome}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1 }}>
        <Box>

          {/* Responsáveis */}
          {subtarefa.responsaveis.length > 0 && (
            <Box mt={1}>
              <AvatarGroup max={3} sx={{ justifyContent: "flex-start" }}>
                {subtarefa.responsaveis.map((r) => (
                  <Tooltip key={r.idUsuario} title={r.nome}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: "0.65rem",
                        bgcolor: "primary.main",
                      }}
                    >
                      {r.iniciais}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            </Box>
          )}

          {/* Horas */}
          <Box display="flex" gap={2} mt={1}>
            <Typography variant="caption" color="textSecondary">
              Estimativa:{" "}
              <strong>{subtarefa.estimativaHoras || "-"}h</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {/* 8.2.1 — exibir o tempo real a partir de totalMinutosReal
                  (minutos inteiros). Fallback: Math.round((horasReal||0)*60). */}
              Real: <strong>{formatTempoReal(subtarefa)}</strong>
            </Typography>
          </Box>

          {/* Ações */}
          <Box display="flex" justifyContent="flex-end" gap={0.5} mt={1}>
            {onOpen && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<OpenInNew />}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(subtarefa.idSubtarefa);
                }}
                sx={{ textTransform: "none", fontSize: "0.75rem" }}
              >
                ABRIR
              </Button>
            )}
            <Tooltip title="Excluir">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(subtarefa.idSubtarefa);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const PainelSubtarefas = ({
  idTarefa,
  subtarefas,
  onRefresh,
  onOpenSubtarefa,
  idReferencia,
  ehTipologia = false,
}: PainelSubtarefasProps) => {
  const { user } = useAuth();
  const { openNotification } = useNavigation();
  const idEmpresa = user?.idEmpresa || 0;

  // Estado local para sub-tarefas (permite reordenação otimista)
  const [localSubtarefas, setLocalSubtarefas] = useState<ISubtarefaDetalhada[]>([]);
  
  // Ref para controlar se estamos em processo de reordenação
  const isReorderingRef = useRef(false);

  // Atualizar estado local quando subtarefas prop mudar
  // Mas NÃO atualizar se estivermos em processo de reordenação
  useEffect(() => {
    console.log("[PainelSubtarefas] useEffect - subtarefas mudou:", {
      isReordering: isReorderingRef.current,
      quantidadeSubtarefas: subtarefas?.length || 0,
      subtarefas: subtarefas?.map(s => ({ id: s.idSubtarefa, nome: s.nome, ordem: s.ordem })),
    });

    // Se estamos reordenando, não atualizar o estado local
    // O estado local já foi atualizado otimisticamente
    if (isReorderingRef.current) {
      console.log("[PainelSubtarefas] useEffect - BLOQUEADO: está reordenando");
      return;
    }

    if (subtarefas && Array.isArray(subtarefas)) {
      // Ordenar por ordem antes de definir no estado local
      const sorted = [...subtarefas].sort((a, b) => a.ordem - b.ordem);
      console.log("[PainelSubtarefas] useEffect - Atualizando localSubtarefas:", {
        quantidade: sorted.length,
        ordem: sorted.map(s => ({ id: s.idSubtarefa, nome: s.nome, ordem: s.ordem })),
      });
      setLocalSubtarefas(sorted);
    } else {
      setLocalSubtarefas([]);
    }
  }, [subtarefas]);

  // Log para debug
  useEffect(() => {
    console.log("[PainelSubtarefas] Props recebidas:", {
      idTarefa,
      subtarefas,
      quantidadeSubtarefas: subtarefas?.length || 0,
      subtarefasArray: Array.isArray(subtarefas) ? "Sim" : "Não",
    });
  }, [idTarefa, subtarefas]);

  // Estados
  const [isLoading, setIsLoading] = useState(false);

  const [statusList, setStatusList] = useState<IStatusFiltro[]>([]);
  const [responsaveis, setResponsaveis] = useState<IResponsavelFiltro[]>([]);

  // Modal Subtarefa
  const [modalAberto, setModalAberto] = useState(false);
  const [formData, setFormData] = useState<FormSubtarefa>(formInicial);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Dirty-state (N2.5): snapshot do formulário ao abrir o modal interno —
  // base para detectar alterações reais (descrição aqui é TextField simples)
  const initialFormDataRef = useRef<FormSubtarefa>(formInicial);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Estados para campos formatados (Estimativa HH:MM e Responsáveis Autocomplete)
  const [estimativaHHMM, setEstimativaHHMM] = useState("");
  const [responsaveisSelecionados, setResponsaveisSelecionados] = useState<IResponsavelFiltro[]>([]);

  // Modal Vincular Tarefa
  const [modalVincularAberto, setModalVincularAberto] = useState(false);

  // Estado para controlar qual Accordion está expandido
  const [expanded, setExpanded] = useState<string | false>(false);

  // Sensors para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Requer 8px de movimento antes de iniciar drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============================================
  // HANDLERS - DRAG AND DROP
  // ============================================

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return; // Não houve mudança de posição
    }

    const oldIndex = localSubtarefas.findIndex(
      (_, i) => `subtarefa-${localSubtarefas[i].idSubtarefa}` === active.id
    );
    const newIndex = localSubtarefas.findIndex(
      (_, i) => `subtarefa-${localSubtarefas[i].idSubtarefa}` === over.id
    );

    if (oldIndex === -1 || newIndex === -1) {
      return; // Índices inválidos
    }

    // Marcar que estamos reordenando para evitar que useEffect sobrescreva
    isReorderingRef.current = true;
    console.log("[PainelSubtarefas] handleDragEnd - Flag marcado como true");

    // Atualização otimista: reordenar localmente primeiro
    const reordered = arrayMove(localSubtarefas, oldIndex, newIndex);
    console.log("[PainelSubtarefas] handleDragEnd - Reordenação otimista:", {
      oldIndex,
      newIndex,
      antes: localSubtarefas.map(s => ({ id: s.idSubtarefa, nome: s.nome, ordem: s.ordem })),
      depois: reordered.map(s => ({ id: s.idSubtarefa, nome: s.nome, ordem: s.ordem })),
    });
    setLocalSubtarefas(reordered);

    try {
      // Preparar dados para reordenação no backend
      // O backend espera IdsOrdenados (array de IDs na ordem desejada)
      const idsOrdenados = reordered.map((st) => st.idSubtarefa);

      const request: IReordenarSubtarefasTarefaRequest = {
        idTarefa,
        idsOrdenados,
      };

      console.log("[PainelSubtarefas] Reordenando sub-tarefas:", {
        idTarefa,
        idsOrdenados,
        quantidade: idsOrdenados.length,
        ordem: reordered.map((st, idx) => ({ id: st.idSubtarefa, nome: st.nome, ordem: idx + 1 })),
      });

      // Chamar serviço de reordenação
      const sucesso = await reordenarSubtarefasTarefa(request);

      console.log("[PainelSubtarefas] Reordenação concluída no backend:", sucesso);

      if (sucesso) {
        // Atualizar a ordem local das sub-tarefas para refletir a nova ordem
        // Isso evita precisar recarregar do backend imediatamente
        const subtarefasAtualizadas = reordered.map((st, index) => ({
          ...st,
          ordem: index + 1, // Atualizar campo ordem localmente
        }));
        setLocalSubtarefas(subtarefasAtualizadas);
        console.log("[PainelSubtarefas] Estado local atualizado com nova ordem:", {
          quantidade: subtarefasAtualizadas.length,
          ordem: subtarefasAtualizadas.map((st, idx) => ({ id: st.idSubtarefa, nome: st.nome, ordem: st.ordem })),
        });

        // Aguardar um pouco antes de recarregar para garantir que o backend processou completamente
        // Isso evita que o useEffect sobrescreva a ordem antes do backend atualizar
        setTimeout(() => {
          console.log("[PainelSubtarefas] handleDragEnd - Recarregando dados do backend...");
          // Recarregar dados do backend para garantir sincronização completa
          onRefresh();
          
          // Resetar flag após um delay adicional para garantir que o useEffect não interfira
          // Aumentar o delay para dar mais tempo ao backend processar e ao useEffect não interferir
          setTimeout(() => {
            isReorderingRef.current = false;
            console.log("[PainelSubtarefas] Flag de reordenação resetada");
          }, 1500); // Aumentado para 1500ms para garantir que o backend processou
        }, 800); // Aumentado para 800ms para dar mais tempo ao backend
      } else {
        // Se falhou, reverter imediatamente
        console.error("[PainelSubtarefas] Reordenação falhou, revertendo...");
        isReorderingRef.current = false;
        setLocalSubtarefas([...subtarefas].sort((a, b) => a.ordem - b.ordem));
      }

      openNotification({
        isOpen: true,
        message: "Sub-tarefas reordenadas com sucesso!",
        type: "success",
      });
    } catch (error) {
      console.error("[PainelSubtarefas] Erro ao reordenar:", error);
      
      // Resetar flag imediatamente em caso de erro
      isReorderingRef.current = false;
      
      // Em caso de erro, reverter para ordem original
      setLocalSubtarefas([...subtarefas].sort((a, b) => a.ordem - b.ordem));

      openNotification({
        isOpen: true,
        message: "Erro ao reordenar sub-tarefas",
        type: "error",
      });
    }
  };

  const handleExpandChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  const carregarDados = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statusListData, respList] = await Promise.all([
        getStatusFiltro(),
        getResponsaveisFiltro(idEmpresa, idReferencia),
      ]);

      setStatusList(statusListData);
      setResponsaveis(respList);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setIsLoading(false);
    }
  }, [idEmpresa, idReferencia]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Dirty-state por snapshot (N2.5): compara o formulário atual (normalizado)
  // com o snapshot capturado ao abrir o modal interno. Aqui a descrição é um
  // TextField simples (sem Quill), então não há campo rico para reduzir.
  useEffect(() => {
    setHasChanges(
      !isEqual(
        normalizeFormData(formData, [], []),
        normalizeFormData(initialFormDataRef.current, [], [])
      )
    );
  }, [formData]);

  // ============================================
  // HANDLER - ESTIMATIVA HH:MM
  // ============================================
  // Conversões HH:MM <-> decimal centralizadas em shared/helpers/horasHHMM

  // Handler para o campo Estimativa HH:MM (componente InputHoraHHMM)
  const handleEstimativaChange = useCallback(
    (decimal: number | undefined, hhmm: string) => {
      setEstimativaHHMM(hhmm);
      setFormData((prev) => ({
        ...prev,
        estimativaHoras: decimal === undefined ? "" : decimal,
      }));
    },
    []
  );

  // ============================================
  // HANDLERS
  // ============================================

  const handleOpenModal = (subtarefa?: ISubtarefaDetalhada) => {
    if (subtarefa) {
      setEditandoId(subtarefa.idSubtarefa);
      const inicial: FormSubtarefa = {
        nome: subtarefa.nome,
        descricao: subtarefa.descricao || "",

        idTarefaStatus: subtarefa.idTarefaStatus,
        previsaoInicio: subtarefa.previsaoInicio?.split("T")[0] || "",
        previsaoTermino: subtarefa.previsaoTermino?.split("T")[0] || "",
        estimativaHoras: subtarefa.estimativaHoras || "",
        idsResponsaveis: subtarefa.responsaveis.map((r) => r.idUsuario),
      };
      setFormData(inicial);
      // Snapshot do formulário (N2.5): base para detectar alterações reais
      initialFormDataRef.current = inicial;
      setHasChanges(false);
      // Sincronizar estados formatados
      setEstimativaHHMM(decimalToHHMM(subtarefa.estimativaHoras));
      const respsSelecionados = responsaveis.filter((r) =>
        subtarefa.responsaveis.some((sr) => sr.idUsuario === r.idUsuario)
      );
      setResponsaveisSelecionados(respsSelecionados);
    } else {
      setEditandoId(null);
      const inicial: FormSubtarefa = {
        ...formInicial,
        idTarefaStatus: statusList.length > 0 ? statusList[0].idTarefaStatus : "",
      };
      setFormData(inicial);
      // Snapshot do formulário (N2.5): base para detectar alterações reais
      initialFormDataRef.current = inicial;
      setHasChanges(false);
      // Limpar estados formatados
      setEstimativaHHMM("");
      setResponsaveisSelecionados([]);
    }
    setModalAberto(true);
  };

  const handleCloseModal = () => {
    // Guard (N2.5): confirma descarte quando há alterações reais
    if (
      hasChanges &&
      !window.confirm(
        "Há alterações não salvas. Deseja realmente fechar sem salvar?"
      )
    ) {
      return;
    }
    setModalAberto(false);
    setEditandoId(null);
    setFormData(formInicial);
    setHasChanges(false);
    setEstimativaHHMM("");
    setResponsaveisSelecionados([]);
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) {
      openNotification({
        isOpen: true,
        message: ehTipologia
          ? "Preencha o nome e a etapa da sub-tarefa"
          : "Preencha o nome da sub-tarefa",
        type: "warning",
      });
      return;
    }


    setIsSaving(true);
    try {
      if (editandoId) {
        // Atualizar
        await atualizarSubtarefa({
          idSubtarefa: editandoId,
          nome: formData.nome.trim(),
          descricao: formData.descricao || undefined,

          idTarefaStatus: formData.idTarefaStatus as number,
          previsaoInicio: formData.previsaoInicio || undefined,
          previsaoTermino: formData.previsaoTermino || undefined,
          estimativaHoras: formData.estimativaHoras
            ? Number(formData.estimativaHoras)
            : undefined,
          idsResponsaveis: formData.idsResponsaveis,
          idsTags: [], // Tags são gerenciadas no modal de detalhe da sub-tarefa
        });
        openNotification({
          isOpen: true,
          message: "Sub-tarefa atualizada com sucesso!",
          type: "success",
        });
      } else {
        // Criar
        const res = await criarSubtarefa({
          idTarefa,
          nome: formData.nome.trim(),
          descricao: formData.descricao || undefined,

          previsaoInicio: formData.previsaoInicio || undefined,
          previsaoTermino: formData.previsaoTermino || undefined,
          estimativaHoras: formData.estimativaHoras
            ? Number(formData.estimativaHoras)
            : undefined,
          ordem: localSubtarefas.length + 1,
          idsResponsaveis: formData.idsResponsaveis,
        });
        openNotification({
          isOpen: true,
          message: "Sub-tarefa criada com sucesso!",
          type: "success",
        });

        // Não fechar o modal ao salvar (item 4 do relatório). Migrar para o modo
        // edição da subtarefa recém-criada para que novos cliques em Salvar
        // atualizem o registro existente em vez de duplicá-lo.
        if (res?.idSubtarefa) {
          setEditandoId(res.idSubtarefa);
        }
      }

      // Mantém o modal aberto; apenas atualiza a lista-pai (estado local do modal
      // não é afetado por onRefresh).
      onRefresh();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao salvar sub-tarefa",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (idSubtarefa: number) => {
    if (!window.confirm("Deseja excluir esta sub-tarefa?")) return;

    try {
      await excluirSubtarefa(idSubtarefa);
      openNotification({
        isOpen: true,
        message: "Sub-tarefa excluída com sucesso!",
        type: "success",
      });
      onRefresh();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao excluir sub-tarefa",
        type: "error",
      });
    }
  };

  // Item 5.A: nas tarefas de TIPOLOGIA, as subtarefas seguintes espelham a descrição
  // da Planificação e não têm edição própria. FASE 04 (item 3): a Planificação é a
  // PRIMEIRA subtarefa da lista.
  const idPrimeiraSubtarefa = (subtarefas || [])[0]?.idSubtarefa;
  const descricaoSomenteLeitura =
    ehTipologia &&
    editandoId != null &&
    idPrimeiraSubtarefa != null &&
    editandoId !== idPrimeiraSubtarefa;

  // Calcular progresso
  const subtarefasConcluidas = (subtarefas || []).filter(
    (s) => s.nomeStatus.toLowerCase().includes("conclu") || s.idTarefaStatus === 4
  ).length;
  const progresso =
    subtarefas && subtarefas.length > 0
      ? Math.round((subtarefasConcluidas / subtarefas.length) * 100)
      : 0;

  // Função para obter cor do status
  const getStatusColor = (
    idStatus: number
  ): "default" | "primary" | "warning" | "success" | "error" => {
    switch (idStatus) {
      case 1:
        return "default"; // Novo
      case 2:
        return "primary"; // Em andamento
      case 3:
        return "warning"; // Aguardando
      case 4:
        return "success"; // Concluído
      default:
        return "default";
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Cabeçalho - Barra de progresso e botões */}
      <Box sx={{ p: 2 }}>
        {/* Barra de progresso */}
        <Box display="flex" alignItems="center" gap={1}>
          <LinearProgress
            variant="determinate"
            value={progresso}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="textSecondary">
            {progresso}%
          </Typography>
        </Box>
        {/* Botões conforme Mockup */}
        <Box display="flex" gap={1} mt={1.5}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Link />}
            onClick={() => setModalVincularAberto(true)}
            sx={{ flex: 1, fontSize: "0.7rem", textTransform: "none" }}
          >
            VINCULAR TAREFA
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ flex: 1, fontSize: "0.7rem", textTransform: "none" }}
          >
            NOVA SUB-TAREFA
          </Button>
        </Box>
      </Box>

      {/* Lista de Sub-tarefas */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : !subtarefas || subtarefas.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary" variant="body2">
              Nenhuma sub-tarefa
            </Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 1 }}
            >
              Adicionar
            </Button>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localSubtarefas.map((st) => `subtarefa-${st.idSubtarefa}`)}
              strategy={verticalListSortingStrategy}
            >
              {localSubtarefas.map((subtarefa) => (
                <SortableAccordion
                  key={subtarefa.idSubtarefa}
                  id={`subtarefa-${subtarefa.idSubtarefa}`}
                  subtarefa={subtarefa}
                  expanded={expanded}
                  onExpandChange={handleExpandChange}
                  onDelete={handleDelete}
                  onOpen={onOpenSubtarefa}
                  getStatusColor={getStatusColor}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </Box>

      {/* Modal de Sub-tarefa */}
      <Dialog open={modalAberto} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editandoId ? "Editar Sub-tarefa" : "Nova Sub-tarefa"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Nome *"
                fullWidth
                size="small"
                value={formData.nome}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, nome: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                fullWidth
                multiline
                rows={2}
                size="small"
                value={formData.descricao}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, descricao: e.target.value }))
                }
                // Item 5.A: descrição das etapas seguintes (tipologia) é somente leitura.
                disabled={descricaoSomenteLeitura}
                helperText={
                  descricaoSomenteLeitura
                    ? "A descrição espelha a Planificação e não pode ser editada aqui."
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.idTarefaStatus}
                  label="Status"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      idTarefaStatus: e.target.value as number,
                    }))
                  }
                  disabled={!editandoId}
                >
                  {statusList.map((status) => (
                    <MenuItem key={status.idTarefaStatus} value={status.idTarefaStatus}>
                      {status.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Previsão Início"
                type="date"
                fullWidth
                size="small"
                value={formData.previsaoInicio}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    previsaoInicio: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Previsão Término"
                type="date"
                fullWidth
                size="small"
                value={formData.previsaoTermino}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    previsaoTermino: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              {/* Estimativa (hora) no formato HH:MM */}
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: "flex", alignItems: "center" }}>
                  Estimativa (hora)
                  <AccessTime sx={{ fontSize: 14, ml: 0.5, color: "#666" }} />
                </Typography>
                <InputHoraHHMM
                  value={estimativaHHMM}
                  onChangeDecimal={handleEstimativaChange}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              {/* Responsáveis - Autocomplete com pesquisa */}
              <Autocomplete
                multiple
                options={responsaveis}
                getOptionLabel={(option) => option.nome}
                value={responsaveisSelecionados}
                onChange={(_, newValue) => {
                  setResponsaveisSelecionados(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    idsResponsaveis: newValue.map((r) => r.idUsuario),
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsáveis"
                    placeholder={responsaveisSelecionados.length === 0 ? "Pesquisar responsável..." : ""}
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.idUsuario}>
                    {option.nome}
                  </li>
                )}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  return options.filter((option) =>
                    option.nome.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                isOptionEqualToValue={(option, value) => option.idUsuario === value.idUsuario}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || (!!editandoId && !hasChanges)}
            startIcon={<Save />}
          >
            {editandoId ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Vincular Tarefa */}
      <ModalVincularTarefa
        isOpen={modalVincularAberto}
        onClose={() => setModalVincularAberto(false)}
        idTarefaPrincipal={idTarefa}
        onTarefaVinculada={onRefresh}
      />
    </Box>
  );
};

